<template>
  <div ref="mapEl" style="width: 100%; height: 100%"></div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

const ZOOM = 17;
const INITIAL_ZOOM = 7;
const MARKER_IMAGE = '/images/marker.png';
const MARKER_ACTIVE_IMAGE = '/images/marker-active.png';

interface Marker {
  position: google.maps.LatLngLiteral;
  title?: string;
}
interface Props {
  markers?: Marker[];
  activeMarker?: number | null;
}
interface Emits {
  (e: 'update:activeMarker', data: number): void;
}
const props = withDefaults(defineProps<Props>(), {
  markers: () => [],
  activeMarker: null,
});

const emit = defineEmits<Emits>();

const { gmapId } = useRuntimeConfig().public.frontkit;
const mapEl = ref<HTMLElement | null>(null);

const loadMaps = async () => {
  if (!window.google?.maps?.importLibrary) {
    setOptions({ key: gmapId, v: 'weekly' });
  }

  const [core, maps, marker] = await Promise.all([
    importLibrary('core'),
    importLibrary('maps'),
    importLibrary('marker'),
  ]);
  return {
    LatLngBounds: core.LatLngBounds,
    Map: maps.Map,
    Marker: marker.Marker,
  };
};

let api: Awaited<ReturnType<typeof loadMaps>> | null = null;
let map: google.maps.Map | null = null;
let markerInstances: google.maps.Marker[] = [];
let clusterer: MarkerClusterer | null = null;

const iconFor = (index: number) =>
  index === props.activeMarker ? MARKER_ACTIVE_IMAGE : MARKER_IMAGE;

const clearMarkers = () => {
  clusterer?.clearMarkers();
  clusterer?.setMap(null);
  clusterer = null;
  markerInstances.forEach((marker) => marker.setMap(null));
  markerInstances = [];
};

const createMarkers = () => {
  if (!api || !map) return;

  clearMarkers();
  markerInstances = props.markers.map((marker, index) => {
    const instance = new api!.Marker({
      position: marker.position,
      title: marker.title,
      icon: iconFor(index),
    });
    instance.addListener('click', () => emit('update:activeMarker', index));
    return instance;
  });
  // кластеризация раньше давалась обёрткой <GmapCluster>
  clusterer = new MarkerClusterer({ map, markers: markerInstances });
};

const fitMarkersBounds = () => {
  if (!api || !map || !props.markers.length) return;

  const bounds = new api.LatLngBounds();
  props.markers.forEach((marker) => bounds.extend(marker.position));
  map.fitBounds(bounds);
  if (props.markers.length < 2) map.setZoom(ZOOM);
};

onMounted(async () => {
  let loaded;
  try {
    loaded = await loadMaps();
  } catch (error) {
    console.error(error);
    return;
  }
  if (!mapEl.value) return;

  api = loaded;
  map = new api.Map(mapEl.value, {
    center: { lat: 0, lng: 0 },
    zoom: INITIAL_ZOOM,
    streetViewControl: false,
  });

  createMarkers();
  fitMarkersBounds();
});

onBeforeUnmount(() => {
  clearMarkers();
  map = null;
});

watch(
  () => props.markers,
  () => {
    createMarkers();
    fitMarkersBounds();
  },
);

watch(
  () => props.activeMarker,
  (value) => {
    markerInstances.forEach((marker, index) => marker.setIcon(iconFor(index)));
    if (value === null || !map) return;

    map.panTo(props.markers[value].position);
    map.setZoom(ZOOM);
  },
);
</script>
